<template lang="pug">
div
  .row
    .col-12(v-if='!isLoading && hasPendingInvoices && !isStatusUninitialized')
      .card.card--plan-details.card-warning(
        style='margin-top: 2rem; margin-bottom: 1rem'
      )
        .card-body
          .data.d-flex.justify-content-between
            .detail.d-flex.flex-row.align-items-center
              .text(style='color: #202020; font-size: 16px') {{ $t('pages.planDetails.completeUpgrade') }}
            .actionStatus.d-flex.flex-row.align-items-center
              .status
                .detail.d-flex.flex-row.align-items-center
                  .type.card-title
                    i.icon.fas.fa-fw.fa-leaf
                    span.text {{ planNamePendingInvoices }}
                  .price
                    span.money-cipher {{ planCurrencySymbol }}
                    span.money-number {{ planPricePendingSubscription[0] }}
                    span.money-cents ,{{ planPricePendingSubscription[1] }}/{{ pendingSubscriptionType }}
              .action
                button.btn.btn-tiny.btn-primary(
                  @click='payPendingSubscription()'
                ) {{ $t('button.pay') }}
                button.btn-link(
                  @click='cancelPendingSubscription()'
                  style='padding-left: 1.5rem'
                ) {{ $t('button.cancel') }}
    .col-12
      .component-title.d-flex.justify-content-between
        span.text {{ $t('pages.planDetails.currentPlan') }}
        span
          button.btn-link(
            @click='cancelPlan()'
            v-if='canCancelPlan && !isStatusUninitialized'
          )
            .text {{ $t('button.cancelPlan') }}
      .card.card--plan-details
        .card-body
          .data.d-flex.justify-content-between
            .detail.d-flex.align-items-center
              .type.card-title
                icon(:name='PlanIcon.getByName(plan.name)')
                span.text.ms-2 {{ getPlanName }}
              .price(v-if='hasDiscountInSubscription')
                span.text-decoration-line-through {{ planCurrencySymbol }}
                span.text-decoration-line-through {{ planPrice[0] }}
                span.money-cents.text-decoration-line-through ,{{ planPrice[1] }}
                span.money-number &nbsp;{{ planCurrencySymbol }}
                span.money-number {{ lastPaidInvoicePrice[0] }}
                span.money-cents ,{{ lastPaidInvoicePrice[1] }}/{{ subscriptionType }}
              .price(v-else)
                span.money-cipher {{ planCurrencySymbol }}
                span.money-number {{ planPrice[0] }}
                span.money-cents ,{{ planPrice[1] }}/{{ subscriptionType }}
            .actionStatus.d-flex.align-items-center
              .status
                .alert-simple(:class='planStatus.class' role='alert')
                  icon(:name='planStatus.icon' v-if='planStatus.icon')
                  span.text {{ planStatus.name }}
              .action
                button#btn_upgrade_plan.btn.btn-primary(
                  @click='upgrade()'
                  type='button'
                  v-if='canUpgradePlan && !isStatusUninitialized'
                ) {{ $t('button.upgrade') }}
                button#btn_reactivate_plan.btn.btn-primary(
                  @click='reactivePlan()'
                  type='button'
                  v-if='canReactivatePlan'
                ) {{ $t('button.reactivatePlan') }}
                button#btn_payment.btn.btn-primary(
                  @click='payPendingSubscription()'
                  type='button'
                  v-if='isStatusUninitialized'
                ) {{ $t('button.pay') }}
          .detail.d-flex(v-if='hasRecurringPlan')
            .text {{ $t('pages.planDetails.recurringCredits', { interval: getPlanIntervalText }) }}
    .col-12(v-if='hasActiveCreditCard')
      .component-title
        span.text {{ $t('pages.planDetails.registeredCard') }}
      .card.card--plan-details
        .card-body
          .data.d-flex.justify-content-between
            .detail.d-flex.align-items-center
              .creditcard-flag.card-title--creditcard
                span.text {{ creditCard.cardBrand }}
                .skeleton.skeleton-lg(v-if='creditcardLoading')
              .creditcard-details
                .skeletonData(v-if='creditcardLoading')
                  .number-card.d-flex
                    span.text ****
                    .skeleton.skeleton-sm.ms-2
                  .expirationDate.d-flex
                    span.text(:style='colorExpirationDate') {{ $t('pages.planDetails.expiresIn') }}
                    .skeleton.skeleton-md.ms-2
                .data-creditcard-details(v-else) 
                  .text **** {{ creditCard.cardLast4Digits }}
                  .text(:style='colorExpirationDate') {{ $t('pages.planDetails.expiresIn') }} {{ expirationDate }}
            .action
              button.btn.btn-thin(@click='changeCreditCard()' type='button') {{ $t('button.changeCard') }}
          .information(v-if='alertUpcomingDueDate')
            .alert-simple.alert-simple-warning(
              :class='alertUpcomingDueDate.class'
              role='alert'
            )
              icon(name='circle-exclamation')
              span.text {{ alertUpcomingDueDate.text }}
    .col-12(v-if='hasInvoices')
      .component-title
        span.text {{ $t('pages.planDetails.invoices') }}
      .card.card--rounded
        .card-body
          .alert-simple.alert-simple--bigger(
            :class='invoiceAlert.class'
            role='alert'
            v-if='invoiceAlert'
          )
            i.icon.fas.fa-fw.fa-leaf
            span.text(v-html='invoiceAlert.text')
          plan-details-card-table(:data='invoices')
          simple-paginator(
            :payload='invoicesPagination'
            @navigate='loadData'
            style='margin-top: 0.625rem'
          )
  CancelSubscriptionModal(ref='cancelSubscriptionModal')
</template>

<script>
import PlanDetailsCardTable from '@/modules/account/components/organisms/card-table/PlanDetailsCardTable.vue';
import SimplePaginator from '@/ui/organisms/SimplePaginator.vue';
import BillingHttp from '@/services/BillingHttp';
import Icon from '@/ui/atoms/Icon.vue';
import moment from 'moment';
import PlanIcon from '@/enums/PlanIcon';
import PlanIntervalText from '@/enums/PlanIntervalText';
import PlanName from '@/enums/PlanName';
import CancelSubscriptionModal from '@/modules/account/components/organisms/modals/CancelSubscriptionModal.vue';

export default {
  name: 'PlanDetails',
  components: {
    Icon,
    PlanDetailsCardTable,
    SimplePaginator,
    CancelSubscriptionModal,
  },
  data() {
    return {
      invoices: {},
      lastPaidInvoice: {},
      pendingInvoices: {} || null,
      invoicesPagination: {},
      filters: {
        search: '',
      },
      creditcardLoading: true,
      PlanIcon,
    };
  },
  props: {
    plan: Object,
    creditCard: [Object, null],
    planStatus: Object,
  },
  async created() {
    this.$loading(true);
    try {
      this.updatePageStrings();
      await this.loadData();

      const shouldRedirect =
        this.$route.query.autoRedirect === 'true' &&
        this.isStatusUninitialized &&
        this.pendingInvoices;

      if (shouldRedirect) {
        window.history.replaceState({}, '', '/business/dashboard');

        requestAnimationFrame(async () => {
          await this.$nextTick();
          this.payPendingSubscription();
        });
      }
    } finally {
      this.$loading(false);
    }
  },
  watch: {
    creditCard(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.creditcardLoading = false;
      }
    },
  },
  computed: {
    isLoading() {
      return this.$loading === true;
    },
    planNamePendingInvoices() {
      if (this.hasPendingInvoices) {
        return this.pendingInvoices.plan.name;
      }
      return '';
    },
    isFreePlan() {
      return this.$store.state.subscription.plan.plan.value_cents === 0
        ? true
        : false;
    },
    canCancelPlan() {
      return (
        this.subscriptionState !== 'cancelled' &&
        this.subscriptionState !== 'active' &&
        !this.isFreePlan
      );
    },
    canUpgradePlan() {
      return this.planStatus.type !== 'cancelled' || this.isFreePlan;
    },
    canReactivatePlan() {
      return this.planStatus.type === 'cancelled' && !this.isFreePlan;
    },
    hasActiveCreditCard() {
      return this.creditCard && this.planStatus.type !== 'cancelled';
    },
    hasRecurringPlan() {
      return this.subscriptionState == 'recurring';
    },
    hasInvoices() {
      return this.invoices.length > 0;
    },
    hasPendingPayment() {
      if (this.hasPendingInvoices) {
        return this.pendingInvoices.value.amount > 0;
      }
      return false;
    },
    hasPendingInvoices() {
      if (Array.isArray(this.pendingInvoices)) {
        return this.pendingInvoices.length > 0;
      }

      if (
        this.pendingInvoices !== null &&
        typeof this.pendingInvoices === 'object' &&
        Object.keys(this.pendingInvoices).length > 0
      ) {
        return true;
      }

      return false;
    },
    subscriptionState() {
      return this.$store.state.subscription.plan.state;
    },
    isStatusUninitialized() {
      return this.$store.state.subscription.plan.state === 'uninitialized';
    },
    invoiceAlert() {
      if (this.hasInvoices) {
        let valueCents =
          this.$store.state.subscription.plan.plan.value_cents || 0;

        if (this.hasDiscountInSubscription) {
          valueCents = this.lastPaidInvoice.paid_value.amount;
        }

        const amount = new Intl.NumberFormat(this.$i18n.locale, {
          style: 'currency',
          currency: this.getCurrency(),
        }).format(valueCents / 100);

        const nextRecurrence = this.$store.state.subscription.plan
          .next_recurrence_at
          ? moment(
              this.$store.state.subscription.plan.next_recurrence_at
            ).format('DD/MM/YYYY')
          : '-';
        switch (this.subscriptionState) {
          case 'recurring':
            return {
              text: this.$t('pages.planDetails.nextInvoice', {
                amount,
                nextRecurrence,
              }),
              class: 'alert-simple-success',
            };
          case 'active':
            return {
              text: this.$t(
                'pages.planDetails.nextInvoiceCancelledRecurrence',
                {
                  amount,
                  nextRecurrence,
                }
              ),
              class: 'alert-simple-success',
            };
          default:
            return {
              text: this.$t('pages.planDetails.lastInvoice', {
                dueDate:
                  this.invoices[0] && this.invoices[0].due_date
                    ? moment(this.invoices[0].due_date).format('DD/MM/YYYY')
                    : '-',
              }),
              class: 'alert-simple-danger',
            };
        }
      }
      return null;
    },
    planPrice() {
      return this.breakPriceInPieces(this.plan.value_cents);
    },
    planCurrencySymbol() {
      return this.plan.currency_symbol;
    },
    lastPaidInvoicePrice() {
      return this.breakPriceInPieces(this.lastPaidInvoice.paid_value.amount);
    },
    planPricePendingSubscription() {
      return this.breakPriceInPieces(this.pendingInvoices?.value?.amount ?? 0);
    },
    expirationDate() {
      if (this.creditCard.cardExpiration) {
        const [year, month] = this.creditCard.cardExpiration.split('/');
        return `${month}/${year}`;
      }
      return null;
    },
    colorExpirationDate() {
      if (
        this.alertUpcomingDueDate &&
        this.alertUpcomingDueDate.class == 'alert-simple-danger'
      ) {
        return { color: '#DC342B' };
      }
      return null;
    },
    alertUpcomingDueDate() {
      if (this.creditCard.cardExpiration) {
        const [year, month] = this.creditCard.cardExpiration
          .split('/')
          .map(Number);
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;

        const totalMonthsDiff =
          (year - currentYear) * 12 + (month - currentMonth);

        if (totalMonthsDiff === 1 || totalMonthsDiff === 0) {
          return {
            text: this.$t('pages.planDetails.cardExpiringSoon'),
            class: 'alert-simple-warning',
          };
        } else if (totalMonthsDiff < 0) {
          return {
            text: this.$t('pages.planDetails.cardExpired'),
            class: 'alert-simple-danger',
          };
        } else {
          return false;
        }
      }
      return null;
    },
    subscriptionType() {
      return this.plan.interval == 12
        ? this.$t('pages.planDetails.year')
        : this.$t('pages.planDetails.month');
    },
    pendingSubscriptionType() {
      if (!this.hasPendingInvoices) {
        return '';
      }

      return this.pendingInvoices.plan.interval == 12
        ? this.$t('pages.planDetails.year')
        : this.$t('pages.planDetails.month');
    },
    getPlanIntervalText() {
      const key = PlanIntervalText.getByInterval(this.plan.interval);
      return this.$t(key, { interval: this.plan.interval });
    },
    getPlanName() {
      const key = PlanName.getByPlanName(this.plan.name);
      return this.$t(key);
    },
    hasDiscountInSubscription() {
      if (!this.lastPaidInvoice || !this.lastPaidInvoice.discount_value) {
        return false;
      }

      return this.lastPaidInvoice.discount_value.amount > 0;
    },
  },
  methods: {
    async loadData(page = 1) {
      this.$loading(true);
      const params = new URLSearchParams({
        q: this.filters.search,
        page,
      });
      const invoices = await BillingHttp.getInvoices(params);
      this.invoices = invoices.data.data;
      this.invoicesPagination = invoices.data;
      try {
        const pendingInvoices = await BillingHttp.getPendingInvoices();
        this.pendingInvoices = pendingInvoices.data;
      } catch (error) {
        this.pendingInvoices = null;
      }

      this.findLastPaidInvoice();
      this.$loading(false);
    },
    breakPriceInPieces(price) {
      return (price / 100).toFixed(2).split('.');
    },
    upgrade() {
      this.$router.push('/account/billing/plan?tab=plans');
    },
    async cancelPlan() {
      this.$refs.cancelSubscriptionModal.$refs.modal.open();
    },
    async cancelPendingSubscription() {
      this.$loading(true);
      try {
        await BillingHttp.cancelPendingSubscription();
      } finally {
        window.location.reload();
        this.$loading(false);
      }
    },
    async reactivePlan() {
      try {
        this.$loading(true);
        const renew = await BillingHttp.reactivatePlan();
        const responseUrl = new URL(this.$route.path, window.location.origin)
          .href;
        window.location.href = `${renew.data.checkout_url}&callBackResponseUrl=${responseUrl}?token=`;
      } catch (e) {
        this.$loading(false);
      }
    },
    async changeCreditCard() {
      try {
        this.$loading(true);
        await BillingHttp.cancelCreditCard();
        const responseUrl = new URL(this.$route.path, window.location.origin)
          .href;
        window.location.href = `${this.invoices[0].checkout_url}&callBackResponseUrl=${responseUrl}?token=`;
      } catch (e) {
        this.$loading(false);
      }
    },
    async payPendingSubscription() {
      this.$loading(true);
      const responseUrl = new URL(this.$route.path, window.location.origin)
        .href;
      window.location.href = `${this.pendingInvoices.checkout_url}&callBackResponseUrl=${responseUrl}?token=`;
    },
    updatePageStrings() {
      this.$store.dispatch('page/setTitle', this.$t('pages.plan.title'));
      this.$store.dispatch('page/setHead', this.$t('pages.plan.head'));
    },
    findLastPaidInvoice() {
      this.lastPaidInvoice =
        this.invoices.find((invoice) => {
          return invoice.status === 'paid' && invoice.paid_value;
        }) ?? {};
    },
    getCurrency() {
      return this.plan.currency_symbol === 'R$' ? 'BRL' : 'USD';
    },
  },
};
</script>
<style scoped>
.skeleton-lg {
  height: 24px;
  width: 130px;
}

.skeleton-sm {
  height: 15px;
  width: 40px;
}

.skeleton-md {
  height: 15px;
  width: 50px;
}

.skeleton {
  background: linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
}

.creditcard-flag {
  margin-right: 10px;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
